import {
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useSharedUiText } from './sharedUiText';
import { cx, sxToStyle } from './utils';

const LISTBOX_MAX_HEIGHT_PX = 256;
const LISTBOX_GAP_PX = 4;
const DEFAULT_NO_OPTIONS_TEXT = '沒有符合的選項';
const DEFAULT_LOADING_TEXT = '載入中...';

interface ListboxPosition {
    top: number;
    left: number;
    width: number;
    openAbove: boolean;
}

function computeListboxPosition(anchor: HTMLElement): ListboxPosition {
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < LISTBOX_MAX_HEIGHT_PX && rect.top > spaceBelow;
    return {
        top: openAbove ? rect.top - LISTBOX_GAP_PX : rect.bottom + LISTBOX_GAP_PX,
        left: rect.left,
        width: rect.width,
        openAbove,
    };
}

function toArray<T>(value: T | T[] | null | undefined): T[] {
    if (Array.isArray(value)) return value;
    return value === null || value === undefined ? [] : [value];
}

/**
 * 單一欄位的下拉選單：輸入文字即時過濾，方向鍵／Enter／Esc 操作。
 * 選項清單用 portal 固定在欄位下方，不會被對話框的捲動區裁掉。
 */
export function Autocomplete<T>(props: any) {
    const t = useSharedUiText();
    const {
        options = [],
        value = null,
        onChange,
        getOptionLabel = (option: T) => String(option ?? ''),
        isOptionEqualToValue = (option: T, selected: T) => option === selected,
        multiple = false,
        renderInput,
        renderOption,
        renderTags,
        disabled = false,
        loading = false,
        noOptionsText = DEFAULT_NO_OPTIONS_TEXT,
        loadingText = DEFAULT_LOADING_TEXT,
        disableClearable = false,
        disableCloseOnSelect = false,
        inputValue: controlledInputValue,
        onInputChange,
        filterOptions,
        className,
        sxProp,
    } = props;
    const isInputControlled = controlledInputValue !== undefined;

    const listboxId = useId();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [position, setPosition] = useState<ListboxPosition | null>(null);

    const selectedValues: T[] = multiple ? toArray<T>(value) : [];
    const selectedLabel =
        !multiple && value !== null && value !== undefined ? getOptionLabel(value) : '';
    const inputText = isInputControlled
        ? controlledInputValue
        : multiple || isTyping
          ? query
          : selectedLabel;
    const activeQuery = isInputControlled ? controlledInputValue : isTyping ? query : '';

    // 呼叫端自己處理搜尋（例如向伺服器查詢）時，選項已經是結果，不再用顯示文字二次過濾
    const visibleOptions: T[] = useMemo(() => {
        if (filterOptions)
            return filterOptions(options, { inputValue: activeQuery, getOptionLabel });
        if (onInputChange || !activeQuery) return options;
        const lowered = activeQuery.toLowerCase();
        return options.filter((option: T) =>
            getOptionLabel(option).toLowerCase().includes(lowered)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, activeQuery, filterOptions, onInputChange]);

    const isSelected = (option: T) =>
        multiple
            ? selectedValues.some((item) => isOptionEqualToValue(option, item))
            : value !== null && value !== undefined && isOptionEqualToValue(option, value);

    const closeListbox = () => {
        setIsOpen(false);
        setIsTyping(false);
        setQuery('');
    };

    const openListbox = () => {
        if (disabled) return;
        setIsOpen(true);
        setActiveIndex(0);
    };

    useLayoutEffect(() => {
        if (!isOpen || !containerRef.current) return undefined;
        const anchor = containerRef.current;
        const update = () => setPosition(computeListboxPosition(anchor));
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [isOpen, visibleOptions.length]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const closeOnOutsidePress = (event: MouseEvent) => {
            const target = event.target as Node;
            if (containerRef.current?.contains(target) || listboxRef.current?.contains(target)) {
                return;
            }
            closeListbox();
        };
        document.addEventListener('mousedown', closeOnOutsidePress);
        return () => document.removeEventListener('mousedown', closeOnOutsidePress);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        listboxRef.current
            ?.querySelector<HTMLElement>('[data-active="true"]')
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, isOpen, visibleOptions.length]);

    const selectOption = (option: T) => {
        if (multiple) {
            const alreadySelected = isSelected(option);
            const next = alreadySelected
                ? selectedValues.filter((item) => !isOptionEqualToValue(option, item))
                : [...selectedValues, option];
            onChange?.(null, next);
            setQuery('');
            if (!disableCloseOnSelect) closeListbox();
            return;
        }
        onChange?.(null, option);
        onInputChange?.(null, getOptionLabel(option), 'reset');
        closeListbox();
    };

    const removeSelected = (index: number) => {
        onChange?.(
            null,
            selectedValues.filter((_, itemIndex) => itemIndex !== index)
        );
    };

    const clearSelection = () => {
        onChange?.(null, null);
        onInputChange?.(null, '', 'clear');
        closeListbox();
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        onInputChange?.(event, event.target.value, 'input');
        setQuery(event.target.value);
        setIsTyping(true);
        setActiveIndex(0);
        if (!isOpen) setIsOpen(true);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!isOpen) openListbox();
                else setActiveIndex((index) => Math.min(index + 1, visibleOptions.length - 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
                break;
            case 'Enter':
                if (isOpen && visibleOptions[activeIndex] !== undefined) {
                    event.preventDefault();
                    selectOption(visibleOptions[activeIndex]);
                }
                break;
            case 'Escape':
                if (isOpen) {
                    event.stopPropagation();
                    closeListbox();
                }
                break;
            case 'Tab':
                closeListbox();
                break;
            default:
        }
    };

    const canClear =
        !multiple && !disableClearable && !disabled && value !== null && value !== undefined;
    const clearButton = canClear ? (
        <button
            type="button"
            className="btn btn-ghost btn-circle btn-xs -mr-1.5"
            aria-label={t('close')}
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearSelection}
        >
            <X className="h-3.5 w-3.5" />
        </button>
    ) : null;

    const inputParams = {
        fullWidth: true,
        disabled,
        inputProps: {
            value: inputText,
            onChange: handleInputChange,
            onFocus: openListbox,
            onClick: openListbox,
            onKeyDown: handleKeyDown,
            role: 'combobox',
            'aria-expanded': isOpen,
            'aria-autocomplete': 'list',
            'aria-controls': isOpen ? listboxId : undefined,
            autoComplete: 'off',
        },
        InputProps: { endAdornment: clearButton },
    };

    const getTagProps = ({ index }: { index: number }) => ({
        onDelete: () => removeSelected(index),
    });

    const renderSelectedTags = (): ReactNode => {
        if (!multiple || selectedValues.length === 0) return null;
        if (renderTags) return renderTags(selectedValues, getTagProps);
        return selectedValues.map((item, index) => (
            <span key={index} className="badge badge-neutral gap-1">
                {getOptionLabel(item)}
                <button type="button" aria-label={t('close')} onClick={() => removeSelected(index)}>
                    <X className="h-3 w-3" />
                </button>
            </span>
        ));
    };

    const renderOptionRow = (option: T, index: number) => {
        const selected = isSelected(option);
        const active = index === activeIndex;
        const rowProps = {
            key: `${index}-${getOptionLabel(option)}`,
            role: 'option',
            'aria-selected': selected,
            'data-active': active,
            className: cx(
                'flex min-h-10 cursor-pointer items-center gap-2 rounded-xl px-3 text-sm',
                active ? 'bg-base-200' : '',
                selected ? 'font-medium text-primary' : ''
            ),
            onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault(),
            onMouseEnter: () => setActiveIndex(index),
            onClick: () => selectOption(option),
        };
        if (renderOption) return renderOption(rowProps, option, { selected });
        const { key, ...restProps } = rowProps;
        return (
            <li key={key} {...restProps}>
                {getOptionLabel(option)}
            </li>
        );
    };

    const listboxContent =
        visibleOptions.length > 0 ? (
            visibleOptions.map(renderOptionRow)
        ) : (
            <li className="px-3 py-2 text-sm text-base-content/70">
                {loading ? loadingText : noOptionsText}
            </li>
        );

    const selectedTags = renderSelectedTags();

    return (
        <div className={className} style={sxToStyle(sxProp)}>
            <div ref={containerRef}>{renderInput?.(inputParams)}</div>
            {selectedTags ? <div className="mt-2 flex flex-wrap gap-2">{selectedTags}</div> : null}
            {isOpen && position
                ? createPortal(
                      <ul
                          ref={listboxRef}
                          id={listboxId}
                          role="listbox"
                          aria-multiselectable={multiple || undefined}
                          className="fixed z-[130] overflow-auto rounded-2xl border border-base-300 bg-base-100 p-1 shadow-xl"
                          style={{
                              left: position.left,
                              width: position.width,
                              maxHeight: LISTBOX_MAX_HEIGHT_PX,
                              ...(position.openAbove
                                  ? { bottom: window.innerHeight - position.top }
                                  : { top: position.top }),
                          }}
                      >
                          {listboxContent}
                      </ul>,
                      document.body
                  )
                : null}
        </div>
    );
}
